import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { usePriceLifecycleAudit } from '../../hooks';
import { formatDateTime, formatPrice } from '../../lib/format';

export interface PriceAuditPanelProps {
  /**
   * The card drawer's "Open audit" deep link (design spec §8.7, `?tab=audit&productId=`) — a
   * REAL server-side filter (`GET lifecycle-audit?productId=`, spec §3.1), unlike the Approvals
   * tab's client-side-only highlight. Renders the same dismissible "Product: {id}" filter chip.
   */
  productId?: string | null;
  /** Dismisses the product-filter chip — clears `?productId=` on the page. */
  onClearProductFilter?: () => void;
}

/** Read-only price-lifecycle audit log. */
export function PriceAuditPanel({ productId, onClearProductFilter }: PriceAuditPanelProps = {}) {
  const { t } = useTranslation('catalog');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const query = usePriceLifecycleAudit({ page, size, productId: productId ?? undefined });
  const entries = query.data?.content ?? [];

  const productFilterChip = productId ? (
    <Chip
      size="small"
      label={t('priceAuditPanel.productFilterChip', { id: productId })}
      onDelete={onClearProductFilter}
      deleteIcon={<CloseRoundedIcon titleAccess={t('priceAuditPanel.clearFilterAria')} />}
      sx={{ mb: 2, fontFamily: 'JetBrains Mono, monospace' }}
    />
  ) : null;

  if (query.isLoading) {
    return (
      <>
        {productFilterChip}
        <LoadingState label={t('priceAuditPanel.loading')} />
      </>
    );
  }
  if (query.isError) {
    return (
      <>
        {productFilterChip}
        <ErrorState
          title={t('priceAuditPanel.loadError.title')}
          message={query.error?.message ?? t('common:error.unknown')}
          onRetry={() => void query.refetch()}
        />
      </>
    );
  }
  if (entries.length === 0) {
    return (
      <>
        {productFilterChip}
        <EmptyState
          title={t('priceAuditPanel.empty.title')}
          description={t('priceAuditPanel.empty.description')}
          icon={<HistoryRoundedIcon />}
        />
      </>
    );
  }

  return (
    <>
      {productFilterChip}
      <TablePageShell
        footer={
          <CompactTablePagination
            count={query.data?.totalElements ?? 0}
            page={page}
            rowsPerPage={size}
            hideRowsPerPage
            onPageChange={(_e, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t('common:pagination.of')} ${count}`
            }
          />
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('priceAuditPanel.table.date')}</TableCell>
                <TableCell>{t('priceAuditPanel.table.product')}</TableCell>
                <TableCell>{t('priceAuditPanel.table.transition')}</TableCell>
                <TableCell>{t('priceAuditPanel.table.action')}</TableCell>
                <TableCell>{t('priceAuditPanel.table.change')}</TableCell>
                <TableCell>{t('priceAuditPanel.table.author')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {entry.productId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {entry.fromState ? (
                        <Chip size="small" variant="outlined" label={entry.fromState} />
                      ) : null}
                      <span>→</span>
                      <Chip size="small" color="primary" variant="outlined" label={entry.toState} />
                    </Stack>
                  </TableCell>
                  <TableCell>{entry.action}</TableCell>
                  <TableCell>
                    {entry.oldValue != null || entry.newValue != null
                      ? `${formatPrice(entry.oldValue)} → ${formatPrice(entry.newValue)}`
                      : '—'}
                  </TableCell>
                  <TableCell>{entry.actor ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TablePageShell>
    </>
  );
}
